import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { useAuth } from '@bymason-private/auth-provider';
import { SimModel, SimState } from '@bymason/controller-api-hooks';
import { Route, useRouter } from '@bymason-private/route-provider';
import { useGetUsageBySim } from '@bymason/controller-api-hooks';
import {
  ControllerDataGrid,
  DataGridInfiniteScroll,
  GridColDef,
  GridDensity,
  GridRowsProp,
  GridSortModel,
  dataGridLinkRendererFactory,
  dataGridTruncationFormatterFactory,
} from '@bymason-private/legacy-pattern-components';
import {
  dataGridCapitalizationFormatter,
  dataGridDataSizeFormatter,
} from '@bymason-private/pattern-components';

const FETCH_ROW_LIMIT = 50;

interface BySimGridProps {
  density: GridDensity;
  page?: number;
  pagination?: boolean;
  reportMonth: Date | null;
  show: boolean;
  statusFilter: SimState | undefined;
  simConnectivityRoute: Route;
  isScrolledToBottom?: boolean;
  onBusyChange: (busy: boolean) => void;
  onDataInfo: (hasNext: boolean) => void;
  onErrorChange: (error: string | null) => void;
  onSortModelChange: () => void;
}

export default function BySimGrid({
  density,
  page = 1,
  pagination,
  reportMonth,
  show,
  statusFilter,
  simConnectivityRoute,
  isScrolledToBottom,
  onBusyChange,
  onDataInfo,
  onErrorChange,
  onSortModelChange,
}: BySimGridProps) {
  const { navigate } = useRouter();
  const { authToken = '' } = useAuth();
  const [getUsageBySim, usageBySim, usageBySimError, usageBySimBusy] = useGetUsageBySim(authToken);
  const [loadedRows, setLoadedRows] = useState<SimModel[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastNextPage, setLastNextPage] = useState('');
  const isLargeDesktop = useMediaQuery('(min-width:1100px)', { noSsr: true });

  const gridColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'iccid',
        filterable: false,
        flex: 0,
        headerName: 'ICCID',
        type: 'string',
        renderCell: dataGridLinkRendererFactory((params) => {
          const iccid = params.value as string;

          navigate(simConnectivityRoute, { iccid });
        }),
        sortable: false,
        valueFormatter: dataGridTruncationFormatterFactory(42),
        minWidth: 336,
      },
      {
        field: 'status',
        filterable: true,
        flex: 0,
        headerName: 'Status',
        sortable: false,
        type: 'string',
        valueFormatter: dataGridCapitalizationFormatter,
        minWidth: isLargeDesktop ? 100 : 80,
      },
      {
        disableExport: true,
        field: 'download',
        filterable: false,
        flex: 0,
        headerName: 'Download',
        type: 'number',
        valueFormatter: dataGridDataSizeFormatter,
        minWidth: isLargeDesktop ? 136 : 116,
      },
      {
        field: 'upload',
        filterable: false,
        flex: 0,
        headerName: 'Upload',
        type: 'number',
        valueFormatter: dataGridDataSizeFormatter,
        minWidth: isLargeDesktop ? 136 : 116,
      },
      {
        field: 'total',
        filterable: false,
        flex: 0,
        headerName: 'Total',
        type: 'number',
        valueFormatter: dataGridDataSizeFormatter,
        minWidth: isLargeDesktop ? 136 : 116,
      },
    ],
    [isLargeDesktop, navigate, simConnectivityRoute]
  );

  const gridRows = useMemo<GridRowsProp>(
    () =>
      loadedRows
        ? loadedRows.map((record) => ({
            id: record.iccid,
            iccid: record.iccid,
            status: record.status,
            download: record.usage?.download || 0,
            upload: record.usage?.upload || 0,
            total: record.usage?.total || 0,
          }))
        : [],
    [loadedRows]
  );

  const handleSort = useCallback(
    (newSortModel: GridSortModel) => {
      setSortModel(newSortModel);
      onSortModelChange();
    },
    [onSortModelChange]
  );

  const handleOnLoadMoreData = useCallback(() => {
    if (
      loadedRows.length <= 5000 &&
      reportMonth &&
      usageBySim?.nextPage &&
      usageBySim?.nextPage !== lastNextPage
    ) {
      const orderBy = sortModel.length ? sortModel[0].sort || undefined : undefined;
      const orderByColumn = sortModel.length ? sortModel[0].field : undefined;

      // make sure multiple next calls aren't inadvertently called
      setLastNextPage(usageBySim.nextPage);

      getUsageBySim({
        after: usageBySim.nextPage,
        limit: FETCH_ROW_LIMIT,
        month: reportMonth,
        orderBy,
        orderByColumn,
        status: statusFilter,
      });
    }
  }, [
    getUsageBySim,
    lastNextPage,
    loadedRows.length,
    reportMonth,
    sortModel,
    statusFilter,
    usageBySim?.nextPage,
  ]);

  //
  // handle retrieving sim data initially and
  // whenever sorting or filtering is changed
  //
  useEffect(() => {
    if (reportMonth) {
      const orderBy = sortModel.length ? sortModel[0].sort || undefined : undefined;
      const orderByColumn = sortModel.length ? sortModel[0].field : undefined;

      getUsageBySim({
        limit: FETCH_ROW_LIMIT,
        month: reportMonth,
        orderBy,
        orderByColumn,
        status: statusFilter,
      });
    }
  }, [getUsageBySim, reportMonth, sortModel, statusFilter]);

  //
  // handle setting data rows
  //
  useEffect(() => {
    if (usageBySim?.data) {
      setLoadedRows((loadedRows) => (pagination ? usageBySim.data : loadedRows.concat(usageBySim.data)));
      onDataInfo(!!usageBySim.nextPage);
    }
  }, [onDataInfo, pagination, usageBySim?.data, usageBySim?.nextPage]);

  //
  // handle resetting loaded rows whenever the
  // report-month, sort column, or filter is changed
  //
  useEffect(() => {
    if (!pagination) {
      setLoadedRows([]);
    }
  }, [pagination, reportMonth, sortModel, statusFilter]);

  //
  // handle page change
  //
  useEffect(() => {
    if (reportMonth && page !== currentPage) {
      const orderBy = sortModel.length ? sortModel[0].sort || undefined : undefined;
      const orderByColumn = sortModel.length ? sortModel[0].field : undefined;

      getUsageBySim({
        before: page < currentPage ? usageBySim?.prevPage : undefined,
        after: page > currentPage ? usageBySim?.nextPage : undefined,
        limit: FETCH_ROW_LIMIT,
        month: reportMonth,
        orderBy,
        orderByColumn,
        status: statusFilter,
      });

      setCurrentPage(page);
    }
  }, [
    currentPage,
    getUsageBySim,
    page,
    reportMonth,
    sortModel,
    statusFilter,
    usageBySim?.nextPage,
    usageBySim?.prevPage,
  ]);

  //
  // handle reporting busy state
  //
  useEffect(() => {
    onBusyChange(usageBySimBusy);
  }, [onBusyChange, usageBySimBusy]);

  //
  // handle reporting error state
  //
  useEffect(() => {
    onErrorChange(usageBySimError);
  }, [onErrorChange, usageBySimError]);

  const [useInfiniteScroll] = useState(!pagination);
  const [useLoadMoreButton] = useState(false);

  return show ? (
    <div>
      {useInfiniteScroll && (
        <DataGridInfiniteScroll
          columns={gridColumns}
          density={density}
          disableSelectionOnClick
          loading={usageBySimBusy}
          loadMoreButton={useLoadMoreButton}
          loadMoreButtonText={`Load next ${FETCH_ROW_LIMIT} records`}
          loadedRowCount={loadedRows.length}
          onLoadMoreData={handleOnLoadMoreData}
          onSortModelChange={handleSort}
          rows={gridRows}
          sortModel={sortModel}
          sortingMode='server'
          isScrolledToBottomOverride={isScrolledToBottom}
        />
      )}
      {!useInfiniteScroll && (
        <ControllerDataGrid
          columns={gridColumns}
          density={density}
          disableSelectionOnClick
          loading={usageBySimBusy}
          onSortModelChange={handleSort}
          rows={gridRows}
          sortModel={sortModel}
          sortingMode='server'
        />
      )}
    </div>
  ) : null;
}
